import { MultiplexPanelRevision, MultiplexPanelView } from 'interfaces/multiplexPanels';
import { StainType } from 'interfaces/stainType';
import { filter, find, includes, map, uniq } from 'lodash';

export const getPanelRnaProbeDisplayNames = (revisions: MultiplexPanelRevision[] = []): Record<string, string> => {
  const latestByPanel = new Map<string, MultiplexPanelRevision>();
  for (const revision of revisions) {
    if (revision.revision > (latestByPanel.get(revision.panelId)?.revision ?? 0)) {
      latestByPanel.set(revision.panelId, revision);
    }
  }
  const names: Record<string, string> = {};
  for (const panel of latestByPanel.values()) {
    for (const channel of panel.channels) {
      if (channel.channelModality === 'rna' && channel.displayName) {
        names[channel.analysisName] = channel.displayName;
      }
    }
  }
  return names;
};

export const applyPanelRnaProbeDisplayNames = (stains: StainType[], names: Record<string, string>): StainType[] =>
  stains.map((stain) => {
    const displayName = names[stain.id] || (stain.rna ? stain.id : stain.displayName);
    return displayName === stain.displayName ? stain : { ...stain, displayName };
  });

export const getPanelChannelDisplayName = (
  panel: MultiplexPanelView,
  markerType: string,
  stains: StainType[],
  fallback: (markerType: string) => string
) => {
  const channel = find(panel.channels, (candidate) => candidate.saved?.analysisName === markerType);
  const saved = channel?.saved;
  if (saved?.channelModality === 'rna') {
    return saved.displayName || markerType;
  }
  return (
    saved?.displayName || find(stains, { id: saved?.stainTypeId || markerType })?.displayName || fallback(markerType)
  );
};

export const getPanelChannelModalityLabels = (panel: MultiplexPanelView, markerType: string, stains: StainType[]) => {
  const labels = map(panel.slides, (slide) => {
    const channelIds = Object.keys(slide.channelMarkerTypes).filter(
      (channelId) => slide.channelMarkerTypes[channelId] === markerType
    );
    return map(channelIds, (channelId) =>
      includes(slide.rnaChannelIds, channelId) ? 'RNA' : find(stains, { id: markerType })?.dna ? 'DNA' : 'mIF'
    );
  });
  return uniq(filter(labels.flat(), Boolean));
};
